import { ArrowClockwise, WarningCircle, Lock, CloudSlash, FileX, Bug } from "@phosphor-icons/react";

/**
 * LoadFailedPanel — replaces the silent "Load failed" toast that hides everything
 * useful about an API failure. Shows status code, endpoint, error message, hint,
 * and a Retry button.
 *
 * Props:
 *   error    — axios error object (e.response, e.config, e.message)
 *   onRetry  — optional callback; if provided, a Retry button is shown
 *   resource — optional human label like "Command Center" for the header
 */
export default function LoadFailedPanel({ error, onRetry, resource = "this view" }) {
  const e = error || {};
  const status = e?.response?.status;
  const url = e?.config?.url || "(unknown endpoint)";
  const baseURL = e?.config?.baseURL || "";
  const fullUrl = baseURL && !/^https?:/.test(url) ? `${baseURL.replace(/\/$/, "")}/${String(url).replace(/^\//, "")}` : url;
  const detail = e?.response?.data?.detail || e?.response?.data?.message || e?.message || "Unknown error";

  // Classify the error so we can give a useful next step
  let Icon = Bug;
  let tone = "rose";
  let title = "Something went wrong";
  let hint = "An unexpected error occurred. Use Retry, or check the browser network tab for details.";

  if (status === 401) {
    Icon = Lock; title = "Session expired";
    hint = "Your login session is no longer valid. Sign out and sign back in.";
  } else if (status === 403) {
    Icon = Lock; title = "Access denied";
    hint = "Your user tier doesn't have permission to view this. Contact your administrator.";
  } else if (status === 404) {
    Icon = FileX; title = "Endpoint not found";
    hint = "The server doesn't expose this endpoint. If you are on production, the live deployment may be older than the latest code — push the newest version from preview via 'Save to GitHub' and redeploy.";
  } else if (status === 500 || status === 502 || status === 503) {
    Icon = WarningCircle; title = `Server error (${status})`;
    hint = "The backend crashed while answering this request. Try again in a moment. If it persists, this needs a backend fix.";
  } else if (status === 429) {
    Icon = WarningCircle; title = "Rate-limited";
    hint = "Too many requests. Wait a few seconds and try again.";
  } else if (!status && /Network Error|timeout|ERR_NETWORK|Failed to fetch/i.test(detail)) {
    Icon = CloudSlash; tone = "amber"; title = "Network unreachable";
    hint = "The browser couldn't reach the API server. Check your internet connection, then retry. If you're on production and the preview app works fine, the production backend may be down — try again, or reach Emergent Support.";
  }

  const toneRing = tone === "amber" ? "border-amber-200 bg-amber-50 text-amber-900" : "border-rose-200 bg-rose-50 text-rose-900";

  return (
    <div className={`max-w-2xl border ${toneRing} rounded-lg p-5`} data-testid="load-failed-panel">
      <div className="flex items-start gap-3">
        <Icon size={28} weight="duotone" className="shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-[15px]">{title}</div>
          <div className="text-[12px] opacity-90 mt-0.5">Couldn't load <strong>{resource}</strong>.</div>
          <div className="mt-3 text-[11px] font-mono bg-white/60 border border-current/20 rounded p-2 break-all">
            <div><span className="opacity-60">URL:</span> {String(fullUrl)}</div>
            <div><span className="opacity-60">Status:</span> {status ?? "—"}</div>
            <div><span className="opacity-60">Detail:</span> {String(detail).slice(0, 280)}</div>
          </div>
          <div className="mt-3 text-[12px] opacity-95">{hint}</div>
          {onRetry && (
            <button
              onClick={onRetry}
              data-testid="load-failed-retry"
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-current/90 text-white text-[12px] font-semibold hover:opacity-90 transition"
              style={{ color: "white", backgroundColor: tone === "amber" ? "#b45309" : "#be123c" }}
            >
              <ArrowClockwise size={13} /> Retry
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
